import React, { useState } from 'react';
import './SquareGridChart.css';
import { PLANET_SYMBOLS, PLANET_COLORS } from '../utils/constants';
import SaveChartModal from './SaveChartModal';
import { saveChart } from '../services/horoscopeChartApi';

const SquareGridChart = ({ horoscopeData, chartSectionRef, detailsSectionRef, chartId = null, isLoggedIn = false, onLoginRequired }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(0); // 0: English, 1: Tamil, 2: Telugu, 3: Malayalam, 4: Kannada, 5: Hindi, 6: Spanish, 7: Japanese, 8: Chinese
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savingChart, setSavingChart] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const LANGUAGES = [
    { code: 'en', name: 'English', label: 'English' },
    { code: 'ta', name: 'Tamil', label: 'தமிழ்' },
    { code: 'te', name: 'Telugu', label: 'తెలుగు' },
    { code: 'ml', name: 'Malayalam', label: 'മലയാളം' },
    { code: 'kn', name: 'Kannada', label: 'ಕನ್ನಡ' },
    { code: 'hi', name: 'Hindi', label: 'हिंदी' },
    { code: 'es', name: 'Spanish', label: 'Español' },
    { code: 'ja', name: 'Japanese', label: '日本語' },
    { code: 'zh', name: 'Chinese', label: '中文' }
  ];

  if (!horoscopeData) {
    return <div className="chart-placeholder">No chart data available</div>;
  }

  // Helper function to get planet name in selected language
  const getPlanetName = (planet) => {
    if (planet.name && Array.isArray(planet.name)) {
      return planet.name[selectedLanguage] || planet.name[0];
    }
    return planet.name;
  };

  // Helper function to get planet name short (first 3 characters)
  const getPlanetNameShort = (planet) => {
    const fullName = getPlanetName(planet);
    return fullName
  };

  // Zodiac signs in clockwise order
  const ZODIAC_ORDER = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];

  // Function to calculate house number based on Lagnam (Ascendant)
  const getHouseNumber = (zodiacSign) => {
    const lagnam = horoscopeData.ascendant_sign; // Get the Lagnam/Ascendant
    const lagnamIndex = ZODIAC_ORDER.indexOf(lagnam);
    const signIndex = ZODIAC_ORDER.indexOf(zodiacSign);
    
    if (lagnamIndex === -1 || signIndex === -1) {
      return '?'; // Fallback if zodiac not found
    }
    
    // Calculate house number: (signIndex - lagnamIndex + 12) % 12
    // House 1 is where Lagnam is, then clockwise to 2, 3, ..., 12
    let houseNum = (signIndex - lagnamIndex + 12) % 12 + 1;
    return houseNum; 
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSaveChart = async (chartName) => {
    // Check if user is logged in
    if (!isLoggedIn) {
      setSaveMessage('');
      setShowSaveModal(false);
      // Trigger login modal
      if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }

    if (!chartId) {
      setSaveMessage(`Error: Chart ID not available. Please generate a new chart first.`);
      console.error('No chartId provided:', { chartId, horoscopeData });
      return;
    }

    setSavingChart(true);
    setSaveMessage('');

    try {
      await saveChart(chartId, chartName);
      setSaveMessage(`✓ Chart "${chartName}" saved successfully!`);
      setShowSaveModal(false);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error) {
      setSaveMessage(`Error: ${error.message}`);
      console.error('Save chart error:', error);
    } finally {
      setSavingChart(false);
    }
  };

  // 12 houses layout in 3x4 grid (4 columns, 3 rows)
  // Top row (left to right): 12, 1, 2, 3
  // Middle row (left to right): 11, (empty), (empty), 4
  // Bottom row (left to right): 10, 9, 8, 5, 6, 7
  // Actually, for a 12-house square with 2x6 or 3x4...
  // Let's use the traditional square layout:
  // Note: The following constants are reference layouts for future enhancements
  // Zodiac signs starting from Pisces (top-left)
  // Pisces (12th house), Aries (1st), Taurus (2nd), Gemini (3rd)
  // const ZODIAC_ORDER = [
  //   'Pisces', 'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo',
  //   'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius'
  // ];

  // House-to-Zodiac mapping (starting from Pisces at house 12)
  // const HOUSE_ZODIAC_MAP = {
  //   12: 'Pisces', 1: 'Aries', 2: 'Taurus', 3: 'Gemini',
  //   4: 'Cancer', 5: 'Leo', 6: 'Virgo', 7: 'Libra',
  //   8: 'Scorpio', 9: 'Sagittarius', 10: 'Capricorn', 11: 'Aquarius'
  // };

  // Grid positions for 12 houses (4 columns x 3 rows)
  // const GRID_POSITIONS = {
  //   12: { row: 0, col: 0 }, 1: { row: 0, col: 1 }, 2: { row: 0, col: 2 }, 3: { row: 0, col: 3 },
  //   11: { row: 1, col: 0 }, 4: { row: 1, col: 3 },
  //   10: { row: 2, col: 0 }, 9: { row: 2, col: 1 }, 8: { row: 2, col: 2 },
  //   7: { row: 2, col: 3 }, 6: { row: 2, col: 4 }, 5: { row: 2, col: 5 }
  // };

  // Standard layout is 4x3 (4 columns, 3 rows)
  // const HOUSE_GRID = [
  //   [12, 1, 2, 3],
  //   [11, null, null, 4],
  //   [10, 9, 8, 7, 6, 5]
  // ];

  // Traditional square chart layout
  // const HOUSES_LAYOUT = [
  //   [12, 1, 2, 3],
  //   [11, 'CENTER', 'CENTER', 4],
  //   [10, 9, 8, 7, 6, 5]
  // ];

  // 12 houses in 4x3 grid starting from top-left as Pisces (12th house)
  // const SQUARE_LAYOUT = {
  //   positions: [
  //     [12, 1, 2, 3],
  //     [11, null, null, 4],
  //     [10, 9, 8, 7, 6, 5]
  //   ]
  // };

  // Clockwise arrangement starting from top-left
  // const GRID_ARRANGEMENT = [
  //   { house: 12, row: 0, col: 0, zodiac: 'Pisces' },
  //   { house: 1, row: 0, col: 1, zodiac: 'Aries' },
  //   { house: 2, row: 0, col: 2, zodiac: 'Taurus' },
  //   { house: 3, row: 0, col: 3, zodiac: 'Gemini' },
  //   { house: 4, row: 1, col: 3, zodiac: 'Cancer' },
  //   { house: 5, row: 2, col: 3, zodiac: 'Leo' },
  //   { house: 6, row: 3, col: 3, zodiac: 'Virgo' },
  //   { house: 7, row: 3, col: 2, zodiac: 'Libra' },
  //   { house: 8, row: 3, col: 1, zodiac: 'Scorpio' },
  //   { house: 9, row: 3, col: 0, zodiac: 'Sagittarius' },
  //   { house: 10, row: 2, col: 0, zodiac: 'Capricorn' },
  //   { house: 11, row: 1, col: 0, zodiac: 'Aquarius' }
  // ];

  // Group planets by zodiac sign (matching planets with their signs)
  const planetsBySign = {};
  horoscopeData.planets.forEach(planet => {
    const sign = planet.sign; // Use the actual zodiac sign from planet data
    if (!planetsBySign[sign]) {
      planetsBySign[sign] = [];
    }
    planetsBySign[sign].push(planet);
  });

  // House grid positions in 4x3 layout with zodiac signs
  // Zodiac arrangement:
  // Top row (left to right): Pisces, Aries, Taurus, Gemini
  // Right column (top to bottom): Gemini, Cancer, Leo, Virgo
  // Bottom row (right to left): Virgo, Libra, Scorpio, Sagittarius
  // Left column (bottom to top): Sagittarius, Capricorn, Aquarius, Pisces
  const HOUSE_POSITIONS = [
    // Top row (left to right)
    { house: 12, row: 0, col: 0, zodiac: 'Pisces' },
    { house: 1, row: 0, col: 1, zodiac: 'Aries' },
    { house: 2, row: 0, col: 2, zodiac: 'Taurus' },
    { house: 3, row: 0, col: 3, zodiac: 'Gemini' },
    
    // Right column (top to bottom)
    { house: 4, row: 1, col: 3, zodiac: 'Cancer' },
    { house: 5, row: 2, col: 3, zodiac: 'Leo' },
    
    // Bottom row (right to left)
    { house: 6, row: 3, col: 3, zodiac: 'Virgo' },
    { house: 7, row: 3, col: 2, zodiac: 'Libra' },
    { house: 8, row: 3, col: 1, zodiac: 'Scorpio' },
    { house: 9, row: 3, col: 0, zodiac: 'Sagittarius' },
    
    // Left column (bottom to top)
    { house: 10, row: 2, col: 0, zodiac: 'Capricorn' },
    { house: 11, row: 1, col: 0, zodiac: 'Aquarius' }
  ];

  // Grid dimensions and layout constants
  const SQUARE_SIZE = 10;      // Size of each house square
  const GRID_COLS = 4;         // 4 columns
  const GRID_ROWS = 4;         // 4 rows (to accommodate all positions)
  const OUTER_PADDING = 2;     // Padding around the grid
  const totalWidth = OUTER_PADDING * 2 + GRID_COLS * SQUARE_SIZE;
  const totalHeight = OUTER_PADDING * 2 + GRID_ROWS * SQUARE_SIZE;

  return (
    <div className="square-grid-chart-container">
      <div className="chart-header">
        <h3>South Indian Horoscope Chart</h3>
        
        <p className="chart-info">
          {horoscopeData.location?.name} | {horoscopeData.birth_date} {horoscopeData.birth_time}
          {horoscopeData.timezone && ` | TZ: ${horoscopeData.timezone}`}
        </p>
        <p className="ascendant-info">
          Your Sign: {horoscopeData.rasi_sign} | Ascendant: {horoscopeData.ascendant_sign} {horoscopeData.moon_star && `| Your Star: ${horoscopeData.moon_star}`}
        </p>
        
        {/* Language Selector and Print Button */}
        <div className="language-and-print-row">
          <div className="language-selector">
            <label htmlFor="language-dropdown">🌐 Language: </label>
            <select
              id="language-dropdown"
              className="language-dropdown"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(Number(e.target.value))}
            >
              {LANGUAGES.map((lang, idx) => (
                <option key={idx} value={idx}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
          <div className="chart-action-buttons">
            <button 
              className="save-btn" 
              onClick={() => {
                if (!isLoggedIn) {
                  if (onLoginRequired) {
                    onLoginRequired();
                  }
                } else {
                  setShowSaveModal(true);
                }
              }}
              title={isLoggedIn ? "Save this chart for future reference" : "Login required to save charts"}
              disabled={savingChart}
            >
              💾 {isLoggedIn ? 'Save Chart' : 'Login to Save'}
            </button>
            <button className="print-btn" onClick={handlePrint} title="Print chart and planet details">
              🖨️ Print Chart
            </button>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}

        {/* Save Chart Modal */}
        <SaveChartModal 
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveChart}
          isLoading={savingChart}
        />
      </div>

      {/* Chart Section */}
      <div ref={chartSectionRef} className="chart-wrapper">
        <svg 
          width={totalWidth * 10} 
          height={totalHeight * 10} 
          className="square-grid-chart"
          viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        >
          {/* Draw outer rectangle */}
          <rect
            x={OUTER_PADDING}
            y={OUTER_PADDING}
            width={GRID_COLS * SQUARE_SIZE}
            height={GRID_ROWS * SQUARE_SIZE}
            fill="white"
            stroke="#333"
            strokeWidth="0.5"
          />

          {/* Draw grid lines and house boxes */}
          {HOUSE_POSITIONS.map((position, idx) => {
            const x = OUTER_PADDING + position.col * SQUARE_SIZE;
            const y = OUTER_PADDING + position.row * SQUARE_SIZE;
            // Get planets by zodiac sign
            const planets = planetsBySign[position.zodiac] || [];
            
            // Calculate dynamic house number based on Lagnam
            const dynamicHouseNumber = getHouseNumber(position.zodiac);

            return (
              <g key={idx}>
                {/* House box */}
                <rect
                  x={x}
                  y={y}
                  width={SQUARE_SIZE}
                  height={SQUARE_SIZE}
                  fill="#f9f9f9"
                  stroke="#ccc"
                  strokeWidth="0.3"
                />

                {/* House number in top-left corner - dynamically calculated based on Lagnam */}
                <text
                  x={x + 0.5}
                  y={y + 1.5}
                  fontSize="0.9"
                  fontWeight="bold"
                  fill="#333"
                  className="house-number"
                >
                  H{dynamicHouseNumber}
                </text>

                {/* Zodiac sign in top-right corner */}
                <text
                  x={x + SQUARE_SIZE - 0.5}
                  y={y + 1.5}
                  fontSize="0.8"
                  fontWeight="bold"
                  fill="#666"
                  textAnchor="end"
                  className="zodiac-sign"
                >
                  {position.zodiac.substring(0, 3)}
                </text>

                {/* Planets in this zodiac sign */}
                {planets.map((planet, pIdx) => {
                  const planetX = x + 1;
                  const planetY = y + 3 + pIdx * 2;
                  // Get the first element if planet.name is an array, otherwise use planet.name
                  const primaryPlanetName = Array.isArray(planet.name) ? planet.name[0] : planet.name;
                  const symbol = PLANET_SYMBOLS[primaryPlanetName] || primaryPlanetName.charAt(0);
                  const color = PLANET_COLORS[primaryPlanetName] || '#999';
                  const planetNameShort = getPlanetNameShort(planet); // Use the helper function
                  // const starInfo = planet.star ? planet.star.substring(0, 3) : ''; // First 3 letters of star
                  // const starPadaInfo = planet.star_pada ? planet.star_pada : ''; // Star pada (1-4)

                  return (
                    <g key={`planet-${position.house}-${primaryPlanetName}`}>
                      {/* Planet circle with symbol */}
                      <circle
                        cx={planetX + 1}
                        cy={planetY}
                        r="1"
                        fill={color}
                        stroke="white"
                        strokeWidth="0.15"
                      />
                      {/* Planet symbol */}
                      <text
                        x={planetX + 1}
                        y={planetY + 0}
                        fontSize="1"
                        fontWeight="bold"
                        fill="white"
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        {symbol}
                      </text>
                      {/* Planet name text */}
                      <text
                        x={planetX + 2}
                        y={planetY + 0.3}
                        fontSize="1"
                        fontWeight="600"
                        fill="#333"
                        className="planet-name"
                      >
                        {planetNameShort}
                      </text>
                      
                      {/* Star information (below planet name) */}
                      {/* {starInfo && (
                        <text
                          x={planetX + 3}
                          y={planetY + 1.2}
                          fontSize="0.65"
                          fontWeight="600"
                          fill="#E67E22"
                          className="star-info"
                        >
                          {starInfo}
                        </text>
                      )} */}
                      
                      {/* Star Pada information (next to star) */}
                      {/* {starPadaInfo && (
                        <text
                          x={planetX + 5}
                          y={planetY + 1.2}
                          fontSize="0.65"
                          fontWeight="600"
                          fill="#C0392B"
                          className="star-pada-info"
                        >
                          P{starPadaInfo}
                        </text>
                      )} */}

                      {/* Tooltip with full information */}
                      <title>
                        {planet.name[0]} - {planet.sign} {planet.degrees.toFixed(2)}°
                        {planet.star ? ` | Star: ${planet.star}` : ''}
                        {planet.star_pada ? ` | Star Pada: ${planet.star_pada}` : ''}
                      </title>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Planet Legend Section */}
      <div className="planet-legend">
        <h4>Planets in Houses</h4>
        <div className="legend-content">
          {horoscopeData.planets.map((planet, index) => {
            const primaryPlanetName = Array.isArray(planet.name) ? planet.name[0] : planet.name;
            return (
              <div key={index} className="legend-item">
                <span
                  className="legend-color"
                  style={{ backgroundColor: PLANET_COLORS[primaryPlanetName] || '#000' }}
                />
                <span className="legend-text">
                  {getPlanetName(planet)}: House {planet.house} ({planet.sign} {planet.degrees.toFixed(2)}°)
                  {planet.star ? ` | Star: ${planet.star}` : ''}
                  {planet.star_planet ? ` | Star Planet: ${planet.star_planet}` : ''}
                  {planet.star_pada ? ` | Star Pada: ${planet.star_pada}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed Planet Table - Organized by House */}
      <div ref={detailsSectionRef} className="planet-details-section">
        <h4>Detailed Planet Information by House</h4>
        <div className="planet-table-wrapper">
          <table className="planet-details-table">
            <thead>
              <tr>
                <th>House</th>
                <th>Planet</th>
                <th>Sign</th>
                <th>Degrees</th>
                <th>Minutes</th>
                <th>Star (Nakshatra)</th>
                <th>Star Planet</th>
                <th>Star Pada</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Create array of houses 1-12 and sort planets by house
                const planetsByHouse = {};
                for (let i = 1; i <= 12; i++) {
                  planetsByHouse[i] = [];
                }
                
                horoscopeData.planets.forEach(planet => {
                  if (planetsByHouse[planet.house]) {
                    planetsByHouse[planet.house].push(planet);
                  }
                });

                // Render rows for each house (1-12) and their planets
                const rows = [];
                for (let house = 1; house <= 12; house++) {
                  const planetsInHouse = planetsByHouse[house];
                  
                  if (planetsInHouse.length === 0) {
                    // Empty house row
                    rows.push(
                      <tr key={`house-${house}`} className="empty-house-row">
                        <td className="house-cell" rowSpan="1">{house}</td>
                        <td colSpan="7" className="empty-house-text">No planets</td>
                      </tr>
                    );
                  } else {
                    // Rows for planets in this house
                    planetsInHouse.forEach((planet, idx) => {
                      const degrees = Math.floor(planet.degrees);
                      const minutes = Math.round((planet.degrees - degrees) * 60);
                      const primaryPlanetName = Array.isArray(planet.name) ? planet.name[0] : planet.name;
                      
                      rows.push(
                        <tr key={`house-${house}-planet-${idx}`} className="planet-row">
                          {idx === 0 && <td className="house-cell" rowSpan={planetsInHouse.length}>{house}</td>}
                          <td className="planet-name-cell">
                            <span 
                              className="planet-color-dot" 
                              style={{ backgroundColor: PLANET_COLORS[primaryPlanetName] || '#000' }}
                            />
                            {getPlanetName(planet)}
                          </td>
                          <td className="sign-cell">{planet.sign}</td>
                          <td className="degrees-cell">{degrees}°</td>
                          <td className="minutes-cell">{minutes}'</td>
                          <td className="star-cell">{planet.star || '-'}</td>
                          <td className="star-planet-cell">{planet.star_planet || '-'}</td>
                          <td className="pada-cell">{planet.star_pada ? `P${planet.star_pada}` : '-'}</td>
                        </tr>
                      );
                    });
                  }
                }
                return rows;
              })()}
            </tbody>
          </table>
        </div>

        {/* Table Legend/Notes */}
        <div className="table-notes">
          <p><strong>Description:</strong></p>
          <ul>
            <li><strong>House:</strong> From Lagnam (House 1) to House 12</li>
            <li><strong>Planet:</strong> Name of the celestial body with color coding</li>
            <li><strong>Sign:</strong> Zodiac sign where planet is positioned</li>
            <li><strong>Degrees & Minutes:</strong> Exact position in the zodiac</li>
            <li><strong>Star (Nakshatra):</strong> Lunar mansion where planet is located</li>
            <li><strong>Star Planet:</strong> Ruler of the nakshatra</li>
            <li><strong>Star Pada:</strong> Quarter (1-4) of the nakshatra where planet is situated</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SquareGridChart;
